import { ShareRoomFragment } from "~/__generated__/graphql"
import { useMemo } from "react"
import { useSearchParams } from "react-router-dom"
import pluralize from "pluralize"
import { cn } from "~/common/cn"
import { avatarColorClass } from "~/common/avatar-color"
import { TextureGrainBackground } from "./components/texture-grain-background"
import logo from "~/images/landing/logo"
import { HostPill } from "./components/room-content-card/host-pill"
import { sortHostsWithDragRacePriority } from "./room-content-card"
import { AppText } from "~/ui/app-text"

const AVATAR_BOX = "h-[72px] w-[72px] shrink-0 rounded-2xl border border-base-white/20"

export const ShareRoomCardScreen = (): JSX.Element => {
  const [searchParams] = useSearchParams()
  const roomTag = document.querySelector("meta[name=room-data]")
  const roomDataContent = roomTag?.getAttribute("content")
  const roomData = useMemo(
    () => (roomDataContent ? (JSON.parse(roomDataContent) as ShareRoomFragment) : null),
    [roomDataContent]
  )

  const name = roomData?.name ?? "Chat Room"
  const inviterUsername = searchParams.get("u")

  const sortedHosts = useMemo(() => {
    if (!roomData) return []
    return sortHostsWithDragRacePriority(
      roomData.roomMembershipsPreview,
      roomData.id,
      inviterUsername
    )
  }, [roomData, inviterUsername])

  const hostPillPlacement = sortedHosts.length < 3 ? "justify-center" : "justify-start"

  const roomStatsText = useMemo(() => {
    const membersCount = roomData?.activeRoomFollowsCount
    if (!membersCount || membersCount < 100) return "Hosted by:"
    const hostsCount = roomData?.activeRoomMembershipsCount ?? 0
    const hostsText = `${hostsCount.toLocaleString()} ${pluralize("host", hostsCount)}`
    const membersText = `${membersCount.toLocaleString()} ${pluralize("member", membersCount)}`
    return `${hostsText} · ${membersText}`
  }, [roomData])

  return (
    <div
      className={cn(
        "relative mx-auto flex h-[188px] w-[360px] flex-col items-center justify-center gap-3 overflow-hidden bg-gradient-to-b from-[#0047FF] to-base-blue",
        {
          "rounded-xl": searchParams.get("rounded") === "true",
        }
      )}
    >
      <TextureGrainBackground className="z-[-1]" />

      <img
        src={logo.src}
        alt="ChatBCC"
        className="absolute right-2 top-3 z-10 h-[20px] object-contain"
      />

      <div className="flex items-center gap-3 px-3 pt-2">
        {roomData?.photoUrl ? (
          <img
            src={roomData.photoUrl}
            className={cn(AVATAR_BOX, "object-cover")}
            alt="Room Avatar"
          />
        ) : (
          <div
            className={cn(AVATAR_BOX, "flex items-center justify-center", avatarColorClass(name))}
          >
            <AppText variant="h3" className="text-[48px] leading-none opacity-70">
              {name.charAt(0).toUpperCase()}
            </AppText>
          </div>
        )}

        <AppText variant="h3" className={cn("line-clamp-3 text-base-white")}>
          <AppText variant="h3" className={cn("max-w-[200px] leading-5")}>
            {name}
          </AppText>
          {roomData?.description && (
            <AppText variant="body2-medium" className="pt-1 text-base-white opacity-80">
              {roomData.description}
            </AppText>
          )}
        </AppText>
      </div>

      <div className="flex flex-col items-center gap-2">
        <AppText
          variant="body3-medium"
          className="w-full px-3 text-center text-base-white opacity-70"
        >
          {roomStatsText}
        </AppText>

        {sortedHosts.length > 0 && (
          <div className={cn("flex items-center gap-1.5 overflow-hidden px-3", hostPillPlacement)}>
            {sortedHosts.slice(0, 4).map(({ user }) => (
              <HostPill key={user.id} member={user} variant="compact-wrap" />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default ShareRoomCardScreen
